import {createContext, useCallback, useContext, useState, ReactNode} from 'react'

type ToastType = 'success' | 'error' | 'info'
interface ToastItem {
  id: number
  message: string
  type: ToastType
}

interface ToastCtx {
  show: (message: string, type?: ToastType) => void
  success: (message: string) => void
  error: (message: string) => void
  info: (message: string) => void
}

const Ctx = createContext<ToastCtx>({show: () => {}, success: () => {}, error: () => {}, info: () => {}})

/** Toast notifications for async success/error feedback. Use everywhere instead of alert(). */
export const useToast = () => useContext(Ctx)

const TONE: Record<ToastType, {bg: string; border: string; icon: string}> = {
  success: {bg: '#ecfdf3', border: '#a6f4c5', icon: '✓'},
  error: {bg: '#fef3f2', border: '#fecaca', icon: '✕'},
  info: {bg: '#eff6ff', border: '#bfdbfe', icon: 'ℹ'},
}
const ICON_COLOR: Record<ToastType, string> = {success: '#039540', error: '#d9214e', info: '#2b4eff'}

let counter = 0

export const ToastProvider = ({children}: {children: ReactNode}) => {
  const [toasts, setToasts] = useState<ToastItem[]>([])

  const remove = useCallback((id: number) => setToasts((t) => t.filter((x) => x.id !== id)), [])

  const show = useCallback(
    (message: string, type: ToastType = 'info') => {
      const id = ++counter
      setToasts((t) => [...t, {id, message, type}])
      setTimeout(() => remove(id), 4000)
    },
    [remove],
  )

  const value: ToastCtx = {
    show,
    success: (m) => show(m, 'success'),
    error: (m) => show(m, 'error'),
    info: (m) => show(m, 'info'),
  }

  return (
    <Ctx.Provider value={value}>
      <style>{`
        @keyframes ccpToastIn { from { opacity: 0; transform: translateY(-8px) } to { opacity: 1; transform: none } }
        @keyframes ccpSpin { to { transform: rotate(360deg) } }
      `}</style>
      {children}
      <div style={{position: 'fixed', top: 20, right: 20, zIndex: 3000, display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 380}}>
        {toasts.map((t) => {
          const tone = TONE[t.type]
          return (
            <div
              key={t.id}
              onClick={() => remove(t.id)}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 11,
                background: tone.bg,
                border: `1px solid ${tone.border}`,
                borderRadius: 12,
                padding: '13px 16px',
                boxShadow: '0 10px 30px rgba(27,27,41,0.12)',
                cursor: 'pointer',
                animation: 'ccpToastIn 0.18s ease',
              }}
            >
              <span style={{width: 22, height: 22, borderRadius: '50%', background: '#fff', color: ICON_COLOR[t.type], fontWeight: 800, fontSize: 13, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0}}>{tone.icon}</span>
              <span style={{fontSize: 14, color: '#1b1b29', lineHeight: 1.45}}>{t.message}</span>
            </div>
          )
        })}
      </div>
    </Ctx.Provider>
  )
}
